import React, { useEffect, useState } from "react";
import { Stethoscope, Search } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { READINESS_COLORS, timeAgo } from "@/pages/fleet/constants";

export default function FleetClinicians() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/fleet/clinicians");
        setItems(r.data.items || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = items.filter((c) =>
    !search || (`${c.name} ${c.role_code} ${c.facility_name || ""}`).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="fleet-clinicians-page">
      <PageHeader title="Clinician Availability" subtitle="Live readiness, workload and capacity for all field clinicians." />
      <div className="hah-card p-3 mb-4 flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg flex-1">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clinicians…"
            className="bg-transparent outline-none text-sm w-full" data-testid="fleet-clin-search" />
        </div>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : (
        <div className="hah-card p-2">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left p-2.5">Clinician</th>
                <th className="text-left p-2.5">Role</th>
                <th className="text-left p-2.5">Facility</th>
                <th className="text-left p-2.5">Skills</th>
                <th className="text-left p-2.5">Status</th>
                <th className="text-left p-2.5">Readiness</th>
                <th className="text-left p-2.5">Workload</th>
                <th className="text-left p-2.5">Updated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const r = READINESS_COLORS[c.readiness] || READINESS_COLORS.unavailable;
                return (
                  <tr key={c.clinician_id} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]">
                    <td className="p-2.5 font-medium text-[#1F2937]">{c.name}</td>
                    <td className="p-2.5 text-[#6B7280] capitalize">{(c.role_code || "").replace("_", " ")}</td>
                    <td className="p-2.5 text-[#6B7280]">{c.facility_name || "—"}</td>
                    <td className="p-2.5 text-[#6B7280] text-xs">{(c.skills || []).join(", ")}</td>
                    <td className="p-2.5 text-[#6B7280] capitalize">{c.status?.replace("_", " ")}</td>
                    <td className="p-2.5">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider" style={{ background: r.bg, color: r.text }}>
                        {r.label}
                      </span>
                    </td>
                    <td className="p-2.5 text-[#6B7280]">{c.workload}/{c.capacity || 6}</td>
                    <td className="p-2.5 text-[#94A3B8] text-xs">{timeAgo(c.updated_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
